'use client';

import React from 'react';

import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { LocaleCurrencyFormat } from '@/components/LocaleCurrencyFormat';
import { type CurrencyCode } from '@/modules/currencies/constants';

type Props = {
  currencyCode: CurrencyCode;
  price: string;
  ariaDescription?: string;
  discountedPrice?: string;
  className?: string;
  inPreview?: boolean;
  variant?: 'default' | 'caption';
};

/**
 * @TECHBEBT PB 1/2/24
 * The render logic in this component is hard to follow and
 * could be simplified.
 */
export function Price({
  currencyCode,
  price,
  discountedPrice,
  className = '',
  ariaDescription: ariaLabel,
  inPreview: _,
  variant = 'default',
}: Props) {
  const t = useTranslations('common');
  const isDiscounted =
    typeof discountedPrice !== 'undefined' && discountedPrice !== null;

  function fullPriceComponent() {
    if (isDiscounted) {
      return (
        <Caption
          variant="lg-regular"
          className={clsx(styles.price, {
            [styles.discountedFullPrice]: isDiscounted,
          })}
          aria-description={ariaLabel || t('FullPrice')}
        >
          <LocaleCurrencyFormat
            currencyCode={currencyCode}
            numberToFormat={price}
          />
        </Caption>
      );
    }

    if (variant === 'caption') {
      return (
        <Caption
          variant="sm-regular"
          colour="base"
          className={clsx(styles.price)}
          aria-description={ariaLabel || t('Price')}
        >
          <LocaleCurrencyFormat
            currencyCode={currencyCode}
            numberToFormat={price}
          />
        </Caption>
      );
    }

    return (
      <Body
        variant="sm-bold"
        className={clsx(styles.price, {
          [styles.discountedFullPrice]: isDiscounted,
        })}
        aria-description={ariaLabel || t('Price')}
      >
        <LocaleCurrencyFormat
          currencyCode={currencyCode}
          numberToFormat={price}
        />
      </Body>
    );
  }

  function discountedPriceComponent() {
    if (variant === 'caption') {
      return isDiscounted ? (
        <Caption
          variant="sm-regular"
          colour="base"
          className={styles.price}
          aria-description={t('DiscountedPrice')}
        >
          <LocaleCurrencyFormat
            currencyCode={currencyCode}
            numberToFormat={discountedPrice}
          />
        </Caption>
      ) : null;
    }

    return isDiscounted ? (
      <Body
        variant="sm-bold"
        type="text"
        className={styles.price}
        aria-description={t('DiscountedPrice')}
      >
        <LocaleCurrencyFormat
          currencyCode={currencyCode}
          numberToFormat={discountedPrice}
        />
      </Body>
    ) : null;
  }

  if (!isDiscounted) {
    return <div className={className}>{fullPriceComponent()}</div>;
  }

  return (
    <div className={clsx(className)}>
      {fullPriceComponent()} {discountedPriceComponent()}
    </div>
  );
}
